import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faShuffle, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { SEOHelmet } from 'components/SEO';
import { usePairNextRoundMutation, usePatchTournamentMutation } from 'store/services/play/playApi';
import type { TournamentState } from 'types/interfaces/play';
import { playerName } from 'types/interfaces/play';
import { recommendedSwissRounds } from 'utils/play/recommendedRounds';
import { playErrorMessage } from 'utils/play/playErrors';
import { playPath } from 'utils/play/inviteUrl';
import { PlayLayout } from '../../layout/PlayLayout';
import { ShareLinkCard } from '../../components/ShareLinkCard';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { EventFacts } from '../Invite/InvitePage';
import AddPlayerSheet from './AddPlayerSheet';
import { tournamentStatusLabel } from '../../playFormat';
import './HostLobby.css';

interface Props {
  code: string;
  state: TournamentState;
}

/**
 * The organizer's lobby while status is LOBBY (Decisions 3, 9, 17, 18; design
 * review round 1 items 4 to 6). Order follows the prototype: invite card,
 * Registered and Decklists in stat boxes, the amber missing-decklist row,
 * event summary with the recommended-rounds Apply (review ruling 5), Just
 * joined with real names under usernames, Add player and All players, then
 * the gradient Pair round 1 pinned to the bottom.
 */
const HostLobbyPage: React.FC<Props> = ({ code, state }) => {
  const navigate = useNavigate();
  const { tournament, players } = state;
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patch, { isLoading: patching }] = usePatchTournamentMutation();
  const [pairNext, { isLoading: pairing }] = usePairNextRoundMutation();

  if (tournament.status !== 'LOBBY') {
    return <Navigate replace to={`${playPath(code, 'host')}?tab=round`} />;
  }

  const active = players.filter((p) => !p.dropped);
  const activeCount = active.length;
  const usesSwiss = tournament.structure === 'SWISS' || tournament.structure === 'SWISS_TOP_CUT';
  const recommended = typeof tournament.recommendedSwissRounds === 'number' ? tournament.recommendedSwissRounds : recommendedSwissRounds(activeCount);
  const showRecommendation = usesSwiss && activeCount >= 2 && recommended !== tournament.swissRounds;
  const decksIn = active.filter((p) => p.hasDecklist).length;
  const missingCount = activeCount - decksIn;
  const recent = [...players].reverse().slice(0, 5);
  const playersTab = `${playPath(code, 'host')}?tab=players`;

  const applyRecommendation = async () => {
    setError(null);
    try {
      await patch({ id: tournament.id, code, body: { swissRounds: recommended } }).unwrap();
    } catch (err) {
      setError(playErrorMessage(err));
    }
  };

  const pairRoundOne = async () => {
    setError(null);
    try {
      await pairNext({ id: tournament.id, code }).unwrap();
      navigate(`${playPath(code, 'host')}?tab=round`);
    } catch (err) {
      setError(playErrorMessage(err, "Couldn't pair round 1."));
    }
  };

  return (
    <PlayLayout title={tournament.name} subtitle={`${tournamentStatusLabel(tournament.status, null, tournament.swissRounds)} · ${activeCount} players`} backTo="/play">
      <SEOHelmet title={`${tournament.name} - Lobby - TradeMagic Play`} description="Organizer lobby." noindex />
      <div className="play-lobby">
        <ShareLinkCard code={code} tournamentName={tournament.name} />

        <div className="play-stat-grid" data-testid="play-lobby-stats">
          <div className="play-stat" data-testid="play-stat-registered">
            <span className="play-stat__label">Registered</span>
            <span className="play-stat__value">{activeCount}</span>
          </div>
          <div className="play-stat" data-testid="play-decklists-in">
            <span className="play-stat__label">Decklists in</span>
            {tournament.decklistRequired ? (
              <span className="play-stat__value">
                {decksIn}
                <span className="play-stat__total"> / {activeCount}</span>
              </span>
            ) : (
              <span className="play-stat__value play-stat__value--muted">Not required</span>
            )}
          </div>
        </div>

        {tournament.decklistRequired && missingCount > 0 && (
          <Link to={playersTab} className="play-missing-row" data-testid="play-missing-decklists">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span className="play-missing-row__text">
              {missingCount} player{missingCount === 1 ? '' : 's'} missing a decklist
            </span>
            <FontAwesomeIcon icon={faChevronRight} />
          </Link>
        )}

        <section className="play-card">
          <div className="play-lobby__status">
            <p className="play-section-label">Your event</p>
            <span className="play-status-chip play-status-chip--purple">{tournamentStatusLabel(tournament.status, null, tournament.swissRounds)}</span>
          </div>
          <h1 className="play-title">{tournament.name}</h1>
          <EventFacts tournament={tournament} />
          {showRecommendation && (
            <div className="play-lobby__recommend" data-testid="play-recommended-rounds">
              <span>
                Recommended for {activeCount} players: {recommended} rounds (currently {tournament.swissRounds})
              </span>
              <button type="button" className="play-text-button" onClick={applyRecommendation} disabled={patching}>
                Apply
              </button>
            </div>
          )}
        </section>

        <p className="play-section-label play-lobby__joined-label">Just joined</p>
        {recent.length === 0 ? (
          <section className="play-card">
            <p className="play-helper">Nobody yet. Share the link to get started.</p>
          </section>
        ) : (
          <ul className="play-card play-card--list play-player-list">
            {recent.map((p) => (
              <li key={p.id} className={`play-player-row ${p.dropped ? 'play-player-row--dropped' : ''}`}>
                <span className="play-avatar-initial play-avatar-initial--small">{playerName(p).slice(0, 1).toUpperCase()}</span>
                <span className="play-player-row__main">
                  <span className="play-player-row__name">{playerName(p)}</span>
                  {(p.firstName || p.lastName) && <span className="play-player-row__sub">{[p.firstName, p.lastName].filter(Boolean).join(' ')}</span>}
                </span>
                {tournament.decklistRequired && (
                  <span className={`play-player-row__right ${p.hasDecklist ? 'play-player-row__sub--ok' : 'play-player-row__sub--warn'}`}>{p.hasDecklist ? 'Decklist in' : 'No decklist'}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="play-lobby__secondary">
          <button type="button" className="play-secondary-button" onClick={() => setShowAdd(true)}>
            Add player
          </button>
          <button type="button" className="play-secondary-button" onClick={() => navigate(playersTab)}>
            All players ({players.length})
          </button>
        </div>
        {error && (
          <p className="play-error" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="play-sticky-cta">
        <PlayPrimaryButton label="Pair round 1" icon={faShuffle} disabled={activeCount < 2 || pairing} loading={pairing} onClick={pairRoundOne} testId="play-pair-round-1" />
      </div>
      {showAdd && <AddPlayerSheet code={code} state={state} onClose={() => setShowAdd(false)} />}
    </PlayLayout>
  );
};

export default HostLobbyPage;
